import {cn} from '@/lib/utils'
import {Loader2Icon, CircleAlertIcon, LayoutListIcon} from 'lucide-react'
import {Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {Empty, EmptyHeader, EmptyMedia, EmptyTitle} from '@/components/ui/empty'
import {PhotoCard} from '@/components/photo-card'
import {NavLinks} from '@/components/nav-links'
import {useAuth} from '@/hooks/auth-provider'
import {useLocalStorage} from '@/hooks/use-local-storage'
import useSWR, {type Fetcher} from 'swr'
import type {PhotoData} from '@/types'

export function Photos({className, ...props}: React.ComponentProps<'div'>) {
  const [period, setPeriod] = useLocalStorage('period', 'day')
  const [booth, setBooth] = useLocalStorage('booth', 'all')
  const [minStatus, setMinStatus] = useLocalStorage('minStatus', '00_engaged')

  if (minStatus && !/^\d\d/.test(minStatus)) setMinStatus('00_engaged')

  const auth = useAuth()

  const fetcher: Fetcher<PhotoData[], string> = (url: string, init = {}) =>
    fetch(url, {...init, headers: auth.header})
      .then(res => res.json())
      .then(res => (res.data ? res.data.reverse() : Promise.reject(res.error)))

  const {data: photos, error, isLoading} = useSWR(`/api/photos?booth=${booth}&period=${period}`, fetcher, {refreshInterval: 60000})

  const shouldBeShown = (p: PhotoData) => p.status >= (minStatus || '')

  if (isLoading)
    return (
      <div className={className} {...props}>
        <Loader2Icon />
      </div>
    )

  if (error) {
    return (
      <Alert>
        <CircleAlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className={cn('flex flex-col w-full h-full', className)} {...props}>
      <div className='m-6 grow-1 flex flex-col gap-6'>
        <div className='flex flex-row flex-wrap gap-1 lg:gap-6'>
          <NavLinks />
          <Select defaultValue={period || 'day'} onValueChange={setPeriod}>
            <SelectTrigger className='w-full lg:max-w-48'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Time Period</SelectLabel>
                <SelectItem value='day'>Today</SelectItem>
                <SelectItem value='day-1'>Yesterday</SelectItem>
                <SelectItem value='month'>This month</SelectItem>
                <SelectItem value='month-1'>Last month</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select defaultValue={booth || ''} onValueChange={setBooth}>
            <SelectTrigger className='w-full lg:max-w-48'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Booth</SelectLabel>
                <SelectItem value='all'>All booths</SelectItem>
                <SelectItem value='planeta'>Planeta</SelectItem>
                <SelectItem value='park'>Park</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select defaultValue={minStatus || 'engaged'} onValueChange={setMinStatus}>
            <SelectTrigger className='w-full lg:max-w-60'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {[
                  '00_engaged',
                  '10_template_selected',
                  '20_payment_started',
                  '30_payment_completed',
                  '40_photo_taken',
                  '50_generated',
                  '60_rated',
                ].map(s => (
                  <SelectItem key={s} value={s}>
                    From status: {s}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <div className='grow-1 flex place-items-center justify-end text-right text-muted-foreground'>
            {photos!.reduce((c, _) => c + 1, 0)} engaged,&nbsp;
            {photos!.reduce((c, p) => c + (p.status >= '50_generated' ? 1 : 0), 0)} generated
          </div>
        </div>
        {!photos?.length && (
          <Empty className='grow-1'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <LayoutListIcon />
              </EmptyMedia>
              <EmptyTitle>No data yet</EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}
        <div className='flex flex-row flex-wrap gap-6'>
          {photos && photos!.filter(p => shouldBeShown(p)).map(p => <PhotoCard key={p.id} photo={p} />)}
        </div>
      </div>
    </div>
  )
}
